import React from 'react'
import styled from '@emotion/styled'
import CoachChatHeader from './CoachChatHeader'
import CoachMediaWrap from './CoachMediaWrap'
import CoachLoadingStates from './CoachLoadingStates'
import CoachTextChatWrap from './CoachTextChatWrap'
import { useCoachChat } from './CoachChatContext'

// Styled component for chat content layout
const ChatContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  gap: 1rem;
  min-height: 0; /* Allow grid to shrink below content size */
`

const MediaContentArea = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent content from overflowing */
`

// Styled component for text-only chat layout
const TextChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
`

/**
 * CoachChatInterface component - main container for active coach chat
 * Based on the candidate chat interface but adapted for coach use
 *
 * @param {Object} props
 * @param {Object} props.candidate - The candidate being chatted with
 * @param {string} props.chatMode - Current chat mode (text, audio, video)
 * @param {Array} props.messages - Array of chat messages
 */
const CoachChatInterface = ({ candidate, chatMode }) => {
  const { connectionState } = useCoachChat()
  const { messages } = connectionState
  const showVideoAudio = chatMode === 'video' || chatMode === 'audio'

  // For text-only chat mode, show just the text chat interface
  if (chatMode === 'text') {
    return (
      <TextChatContainer className="text-chat-container">
        <CoachChatHeader candidate={candidate} chatMode={chatMode} />
        <CoachTextChatWrap messages={messages} candidate={candidate} />
      </TextChatContainer>
    )
  }

  // For audio/video modes, show media with text chat
  return (
    <ChatContentContainer className="chat-content-container">
      <CoachChatHeader candidate={candidate} chatMode={chatMode} />
      <MediaContentArea className="media-content-area">
        <CoachLoadingStates
          chatMode={chatMode}
          hasLocalVideo={connectionState.hasLocalVideo}
          hasRemoteVideo={connectionState.hasRemoteVideo}
          cameraReady={connectionState.cameraReady}
        />
        {showVideoAudio && connectionState.cameraReady && (
          <CoachMediaWrap
            chatMode={chatMode}
            candidate={candidate}
            bgEffectsEnabled={false}
          />
        )}
      </MediaContentArea>
      {/* Add text chat for audio mode as well */}
      {chatMode === 'audio' && (
        <CoachTextChatWrap messages={messages} candidate={candidate} />
      )}
    </ChatContentContainer>
  )
}

export default CoachChatInterface
